/**
 * Client-side Unit Service
 *
 * Handles API calls to unit endpoints from the browser.
 * Uses Firebase Auth tokens for authentication.
 *
 * @see functions/src/services/units.ts for backend implementation
 */

import { auth } from '@/lib/firebase';

// ============================================
// TYPES
// ============================================

export interface Unit {
  id: string;
  locationId: string;
  name: string;
  internalName?: string;

  // Guesty integration
  guestyListingId?: string;
  guestyAccountId?: string;
  syncEnabled: boolean;
  guestyEnabled?: boolean; // Master switch: false = use local pricing only
  syncStatus?: 'pending' | 'synced' | 'error' | 'disabled';
  lastSyncAt?: string | null;
  lastSyncError?: string;

  // Description
  description?: string;
  amenities: string[];
  bedConfiguration: string;
  bathroomCount: number;
  images: string[];
  imageAlts: string[];

  // Capacity & Pricing
  pricing: {
    basePrice: { amount: number; currency: string };
    cleaningFee?: { amount: number; currency: string };
    extraGuestPrice: { amount: number; currency: string };
    minimumNights: number;
  };
  baseOccupancy: number;
  maxOccupancy: number;

  // Physical characteristics
  floorLevel?: number;
  size?: {
    value: number;
    unit: 'sqm' | 'sqft';
  };
  view?: string;

  // Extended Guesty data
  spaceDescription?: string;
  neighborhoodDescription?: string;
  accessInstructions?: string;
  transitInfo?: string;
  additionalNotes?: string;
  address?: {
    full?: string;
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    zipcode?: string;
    lat?: number;
    lng?: number;
  };
  timezone?: string;
  bedrooms?: number;
  beds?: number;
  propertyType?: string;
  roomType?: string;
  syncedFromGuesty?: boolean;

  // Classification
  unitType?: 'room' | 'full_lodge' | 'premium';
  amenitiesOverride?: boolean;

  // Admin overrides — fields with overrides[field] === true are NOT
  // overwritten on Guesty webhook re-sync. Identity/system fields
  // (id, slug, locationId, guestyListingId) are always trusted from Guesty.
  overrides?: Record<string, boolean>;

  // State
  sortOrder: number;
  active: boolean;
  useDailyPricing: boolean;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy?: string;
  updatedBy?: string;
  deletedAt?: string | null;
  deletedBy?: string;

  // Location info (populated by GET single unit)
  location?: {
    name: string;
    slug: string;
    location: string;
  };
  isImplicit?: boolean;
}

export interface ListUnitsQuery {
  locationId?: string;
  active?: 'true' | 'false' | 'all';
  guestyOnly?: 'true' | 'false';
  sortBy?: 'name' | 'sortOrder' | 'createdAt' | 'maxOccupancy';
  sortDir?: 'asc' | 'desc';
  limit?: number;
}

export interface CreateUnitInput {
  locationId: string;
  name: string;
  internalName?: string;

  // Guesty integration
  guestyListingId?: string;
  guestyAccountId?: string;
  syncEnabled?: boolean;
  guestyEnabled?: boolean; // Master switch: false = use local pricing only

  // Description
  description?: string;
  amenities?: string[];
  bedConfiguration?: string;
  bathroomCount?: number;
  images?: string[];
  imageAlts?: string[];

  // Capacity & Pricing
  pricing: {
    basePrice: { amount: number; currency: string };
    cleaningFee?: { amount: number; currency: string };
    extraGuestPrice: { amount: number; currency: string };
    minimumNights: number;
  };
  baseOccupancy?: number;
  maxOccupancy: number;

  /** Number of bedrooms (used for the public stats section) */
  bedrooms?: number;

  /** Total number of beds */
  beds?: number;

  // Physical characteristics
  floorLevel?: number;
  size?: {
    value: number;
    unit: 'sqm' | 'sqft';
  };
  view?: string;

  // Classification
  unitType?: 'room' | 'full_lodge' | 'premium';
  amenitiesOverride?: boolean;

  /** Per-field admin override map. See Unit.overrides for semantics. */
  overrides?: Record<string, boolean>;

  // State
  active?: boolean;
  useDailyPricing?: boolean;
}

export type UpdateUnitInput = Partial<Omit<CreateUnitInput, 'locationId'>>;

export interface UnitAvailability {
  unitId: string;
  unitName: string;
  locationId: string;
  startDate: string;
  endDate: string;
  available: boolean;
  blockedDates: string[];
  dateDetails: Record<string, { status: string; bookingId?: string }>;
}

// ============================================
// HELPER FUNCTIONS
// ============================================

/**
 * Get the current Firebase Auth ID token
 */
async function getAuthToken(): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) return null;
  return user.getIdToken();
}

/**
 * Build query string from parameters
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function buildQueryString(params: Record<string, any>): string {
  const filtered = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null)
    .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);

  return filtered.length > 0 ? `?${filtered.join('&')}` : '';
}

/**
 * Get API base URL
 * Note: NEXT_PUBLIC_API_URL is the Cloud Function base URL without /api prefix
 */
function getApiUrl(): string {
  const apiBase = process.env.NEXT_PUBLIC_API_URL;
  return apiBase ? `${apiBase}/api` : '/api';
}

// ============================================
// API FUNCTIONS
// ============================================

/**
 * List units with filters
 */
export async function listUnits(query: ListUnitsQuery = {}): Promise<Unit[]> {
  const queryString = buildQueryString(query);
  const apiUrl = getApiUrl();
  // cache: 'no-store' bypasses the browser HTTP cache. The backend sets
  // Cache-Control: max-age=30, stale-while-revalidate=120, which made admin
  // edits look like they hadn't saved when the post-PUT GET returned a stale
  // cached response. Server-side in-memory cache is invalidated on writes.
  const response = await fetch(`${apiUrl}/units${queryString}`, {
    method: 'GET',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: { message: 'Failed to list units' } }));
    throw new Error(errorData.error?.message || 'Failed to list units');
  }

  const data = await response.json();
  return data.data?.units || [];
}

/**
 * Get a single unit by ID. Sends the admin Bearer token when available so the
 * backend can return inactive units to authenticated admins (public callers
 * still get 404 for inactive units).
 */
export async function getUnit(unitId: string): Promise<Unit> {
  const token = await getAuthToken();
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/units/${unitId}`, {
    method: 'GET',
    cache: 'no-store',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: { message: 'Unit not found' } }));
    throw new Error(errorData.error?.message || 'Unit not found');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get unit availability
 */
export async function getUnitAvailability(
  unitId: string,
  startDate: string,
  endDate: string
): Promise<UnitAvailability> {
  const queryString = buildQueryString({ startDate, endDate });
  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/units/${unitId}/availability${queryString}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json' },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: { message: 'Failed to get availability' } }));
    throw new Error(errorData.error?.message || 'Failed to get availability');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Create a new unit (admin only)
 */
export async function createUnit(input: CreateUnitInput): Promise<Unit> {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/units`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: { message: 'Failed to create unit' } }));
    throw new Error(errorData.error?.message || 'Failed to create unit');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Update a unit (admin only)
 */
export async function updateUnit(unitId: string, input: UpdateUnitInput): Promise<Unit> {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/units/${unitId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify(input),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: { message: 'Failed to update unit' } }));
    throw new Error(errorData.error?.message || 'Failed to update unit');
  }

  const data = await response.json();
  return data.data;
}

/**
 * Delete (soft delete) a unit (admin only)
 */
export async function deleteUnit(unitId: string): Promise<{ message: string }> {
  const token = await getAuthToken();
  if (!token) throw new Error('Authentication required');

  const apiUrl = getApiUrl();
  const response = await fetch(`${apiUrl}/units/${unitId}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({ error: { message: 'Failed to delete unit' } }));
    throw new Error(errorData.error?.message || 'Failed to delete unit');
  }

  const data = await response.json();
  return data.data;
}
